import { useEffect, useRef, useState } from 'react';
import type { MemorySnapshotSection } from '../../agents/agentMemoryApi.ts';
import { TYPE, memoryNodeType, type MemTier } from './memory-graph.ts';
import PanelEmpty from './PanelEmpty.tsx';
import './MemoryList.css';

interface MemoryListProps {
  /** The agent's stored memory, grouped by module, from the `/memory/events`
   *  snapshot (the same data the graph renders). */
  sections: readonly MemorySnapshotSection[];
}

/** Display tiers for the list. `session` is the current-conversation module
 *  (its own snapshot file, not long-term memory), so it gets its own label
 *  rather than collapsing into Episodic. The rest mirror the graph's tiers. */
type ListTier = MemTier | 'session';

/** Tier display metadata — colour mirrors the graph legend (`TYPE` in
 *  memoryGraph.ts) so the list and graph read as one system. */
const TIER_META: Record<ListTier, { label: string; color: string }> = {
  session: { label: 'Session', color: TYPE.prompt.color },
  episodic: { label: 'Episodic', color: TYPE.response.color },
  semantic: { label: 'Semantic', color: TYPE.fact.color },
  meta: { label: 'Meta', color: TYPE.reflection.color },
};

/** Render order; also the de-dup key set. */
const TIER_ORDER: readonly ListTier[] = ['session', 'episodic', 'semantic', 'meta'];

/** One merged tier group for display. */
interface TierGroup {
  tier: ListTier;
  records: MemorySnapshotSection['records'];
  trimmed: number;
}

/** Merge the snapshot sections by their *resolved tier*, so two distinct module
 *  ids that both map to e.g. "Episodic" render as ONE block — the fix for the
 *  duplicate-tier bug (the list previously keyed blocks by raw module id but
 *  labelled them by a fuzzy tier map). */
function groupByTier(sections: readonly MemorySnapshotSection[]): TierGroup[] {
  const byTier = new Map<ListTier, TierGroup>();
  for (const s of sections) {
    const tier = moduleTier(s.moduleId);
    const g = byTier.get(tier) ?? { tier, records: [], trimmed: 0 };
    g.records = [...g.records, ...s.records];
    g.trimmed += s.trimmed;
    byTier.set(tier, g);
  }
  return TIER_ORDER.filter((t) => byTier.has(t)).map((t) => byTier.get(t) as TierGroup);
}

/** Best-effort map of a snapshot module id to a display tier (modules name
 *  themselves after their tier — `session`, `episodic`, `semantic`; anything
 *  unrecognised reads as episodic). The `session` module is the live
 *  conversation, distinct from long-term episodic memory. */
function moduleTier(moduleId: string): ListTier {
  const id = moduleId.toLowerCase();
  if (id.includes('session') || id.includes('working') || id.includes('conversation')) {
    return 'session';
  }
  if (id.includes('semant') || id.includes('fact')) {
    return 'semantic';
  }
  if (id.includes('meta') || id.includes('reflect')) {
    return 'meta';
  }
  return 'episodic';
}

/** Compact, locale-independent timestamp: `2026-01-01 00:00`. */
function stamp(ts: string): string {
  return ts.includes('T') ? ts.replace('T', ' ').slice(0, 16) : ts;
}

/**
 * Inspector "Memory" section, "All" scope: the agent's complete stored memory
 * as a list of collapsible tier groups (one per snapshot module — Session,
 * Episodic, Semantic, Meta). Each group expands to its records, and each
 * record expands on its own to its full text + timestamp. Complements the
 * conversation-scoped graph; both read the same snapshot. The section header +
 * scope toggle live in the inspector accordion, so this renders body content only.
 */
export default function MemoryList({ sections }: MemoryListProps) {
  // Start collapsed: opening "All" shows a tidy list of tier headers —
  // EXCEPT when there's exactly one section (the guest console's session
  // tier): a lone header would just be an extra click (user directive).
  const [open, setOpen] = useState<Set<string>>(() =>
    sections.length === 1 ? new Set([sections[0].moduleId]) : new Set(),
  );
  // Records within an open tier start collapsed too, expanding one at a time
  // to their full text + timestamp. Keyed by `moduleId:recordId` so ids that
  // repeat across modules don't collide.
  const [openRecs, setOpenRecs] = useState<Set<string>>(() => new Set());

  // Guest sections arrive AFTER mount (streamed memory writes) — auto-open a
  // lone section the first time it appears, without fighting a manual
  // collapse afterwards.
  const seenLoneRef = useRef<string | null>(null);
  useEffect(() => {
    const lone = sections.length === 1 ? sections[0] : undefined;
    if (lone !== undefined && seenLoneRef.current !== lone.moduleId) {
      seenLoneRef.current = lone.moduleId;
      setOpen((prev) => new Set(prev).add(lone.moduleId));
    }
  }, [sections]);

  const toggle = (moduleId: string): void => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  };

  const toggleRec = (key: string): void => {
    setOpenRecs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const total = sections.reduce((n, s) => n + s.records.length, 0);
  if (total === 0) {
    return (
      <section className="om-memlist om-memlist--empty">
        <PanelEmpty>
          The agent&apos;s stored memory will appear here, grouped by tier, as it learns.
        </PanelEmpty>
      </section>
    );
  }

  return (
    <section className="om-memlist">
      {groupByTier(sections).map((group) => {
        const tier = TIER_META[group.tier];
        const isOpen = open.has(group.tier);
        return (
          <div
            key={group.tier}
            className={`om-memtier${isOpen ? ' is-open' : ''}`}
          >
            <button
              type="button"
              className="om-memtier__head"
              aria-expanded={isOpen}
              onClick={() => toggle(group.tier)}
            >
              <span className="om-memtier__chevron" aria-hidden="true">
                ▾
              </span>
              <span className="om-memtier__dot" style={{ background: tier.color }} />
              <span className="om-memtier__name">{tier.label}</span>
              <span className="om-memtier__count">{group.records.length}</span>
            </button>
            {isOpen ? (
              <div className="om-memtier__body">
                {group.records.map((rec) => {
                  const type = memoryNodeType(rec.kind);
                  const recKey = `${group.tier}:${rec.id}`;
                  const recOpen = openRecs.has(recKey);
                  return (
                    <div
                      className={`om-memrec${recOpen ? ' is-open' : ''}`}
                      key={rec.id}
                    >
                      <button
                        type="button"
                        className="om-memrec__head"
                        aria-expanded={recOpen}
                        onClick={() => toggleRec(recKey)}
                      >
                        <span
                          className="om-memrec__kind"
                          style={{ color: TYPE[type].color, borderColor: TYPE[type].color }}
                        >
                          {rec.kind}
                        </span>
                        <span className="om-memrec__text">{rec.text}</span>
                        <span className="om-memrec__chevron" aria-hidden="true">
                          ▾
                        </span>
                      </button>
                      {recOpen ? (
                        <div className="om-memrec__meta">{stamp(rec.ts)}</div>
                      ) : null}
                    </div>
                  );
                })}
                {group.trimmed > 0 ? (
                  <div className="om-memtier__trimmed">
                    +{group.trimmed} older record{group.trimmed === 1 ? '' : 's'} not shown
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}
